import { useState, useEffect, useRef } from 'react';
import { Search, X, Loader2 } from 'lucide-react';
import type { User } from 'firebase/auth';
import { fetchSearchData } from '../lib/search';
import GameCard from './GameCard';
import type { Game } from '../hooks/useGames';
import log from '../utils/logger';

interface SearchPageProps {
    isOpen: boolean;
    onClose: () => void;
    onGameSelect: (game: Game) => void;
    onGamePlay: (game: Game) => void;
    user: User | null;
    suggestions?: Game[];
}

const SearchPage = ({ isOpen, onClose, onGameSelect, onGamePlay, user, suggestions }: SearchPageProps) => {
    const [query, setQuery] = useState('');
    const [allSearchResults, setAllSearchResults] = useState<Game[]>([]); // Tutti i risultati dall'API
    const [filteredResults, setFilteredResults] = useState<Game[]>([]); // Risultati filtrati localmente
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [lastSearchQuery, setLastSearchQuery] = useState(''); // Ultima query cercata via API
    const inputRef = useRef<HTMLInputElement>(null);

    // Focus automatico quando si apre
    useEffect(() => {
        if (isOpen && inputRef.current) {
            inputRef.current.focus();
        }
    }, [isOpen]);

    // Reset quando si chiude
    useEffect(() => {
        if (!isOpen) {
            setQuery('');
            setAllSearchResults([]);
            setFilteredResults([]);
            setError(null);
            setLastSearchQuery('');
        }
    }, [isOpen]);

    // Gestione ricerca ottimizzata
    useEffect(() => {
        const handleSearch = async () => {
            const trimmedQuery = query.trim();

            // Se meno di 3 caratteri, cancella tutto
            if (trimmedQuery.length < 3) {
                setAllSearchResults([]);
                setFilteredResults([]);
                setError(null);
                setLastSearchQuery('');
                return;
            }

            // Se esattamente 3 caratteri E diverso dall'ultima ricerca, fai la chiamata API
            if (trimmedQuery.length === 3 && trimmedQuery !== lastSearchQuery) {
                setIsLoading(true);
                setError(null);

                try {
                    const results = await fetchSearchData(trimmedQuery, user || undefined);
                    // I risultati sono già oggetti Game completi
                    setAllSearchResults(results as Game[]);
                    setFilteredResults(results as Game[]);
                    setLastSearchQuery(trimmedQuery);
                } catch (err) {
                    log.error('Errore nella ricerca:', err);
                    setError('Errore nella ricerca');
                    setAllSearchResults([]);
                    setFilteredResults([]);
                } finally {
                    setIsLoading(false);
                }
            }
            // Se esattamente 3 caratteri MA uguale all'ultima ricerca, mostra i risultati esistenti
            else if (trimmedQuery.length === 3 && trimmedQuery === lastSearchQuery) {
                // Non fare nulla, i risultati sono già caricati
                return;
            }
            // Se più di 3 caratteri e abbiamo risultati per i primi 3, filtra localmente
            else if (trimmedQuery.length > 3 && lastSearchQuery === trimmedQuery.substring(0, 3)) {
                const filtered = allSearchResults.filter(game =>
                    game.name.toLowerCase().includes(trimmedQuery.toLowerCase()) ||
                    game.provider.toLowerCase().includes(trimmedQuery.toLowerCase()) ||
                    game.info?.theme?.some(theme =>
                        theme.toLowerCase().includes(trimmedQuery.toLowerCase())
                    ) ||
                    game.info?.functionality?.some(func =>
                        func.toLowerCase().includes(trimmedQuery.toLowerCase())
                    )
                );
                setFilteredResults(filtered);
            }
            // Se più di 3 caratteri ma i primi 3 sono diversi dall'ultima ricerca, fai nuova chiamata API
            else if (trimmedQuery.length > 3 && lastSearchQuery !== trimmedQuery.substring(0, 3)) {
                const firstThreeChars = trimmedQuery.substring(0, 3);
                setIsLoading(true);
                setError(null);

                try {
                    const results = await fetchSearchData(firstThreeChars, user || undefined);
                    // I risultati sono già oggetti Game completi
                    setAllSearchResults(results as Game[]);
                    
                    // Filtra subito con la query completa
                    const filtered = (results as Game[]).filter(game =>
                        game.name.toLowerCase().includes(trimmedQuery.toLowerCase()) ||
                        game.provider.toLowerCase().includes(trimmedQuery.toLowerCase()) ||
                        game.info?.theme?.some(theme =>
                            theme.toLowerCase().includes(trimmedQuery.toLowerCase())
                        ) ||
                        game.info?.functionality?.some(func =>
                            func.toLowerCase().includes(trimmedQuery.toLowerCase())
                        )
                    );
                    setFilteredResults(filtered);
                    setLastSearchQuery(firstThreeChars);
                } catch (err) {
                    log.error('Errore nella ricerca:', err);
                    setError('Errore nella ricerca');
                    setAllSearchResults([]);
                    setFilteredResults([]);
                } finally {
                    setIsLoading(false);
                }
            }
        };

        const debounceTimer = setTimeout(handleSearch, 300);
        return () => clearTimeout(debounceTimer);
    }, [query, user, lastSearchQuery, allSearchResults]);

    const handleClose = () => {
        setQuery('');
        setAllSearchResults([]);
        setFilteredResults([]);
        setError(null);
        setLastSearchQuery('');
        onClose();
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Escape') {
            handleClose();
        }
    };

    const handleGameCardClick = (game: Game) => {
        onGameSelect(game);
        handleClose();
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 bg-search-gray flex flex-col">
            {/* Header con barra di ricerca */}
            <div className="flex justify-end px-8 pt-4">
                <button
                    onClick={handleClose}
                    className="text-white hover:text-gray-300 p-2 rounded-full hover:bg-gray-800 transition-colors"
                >
                    <X size={24} />
                </button>
            </div>
            <div className="px-8">
                <div className="flex items-center">
                    <div className="flex-1 relative">
                        <Search size={20} className="absolute top-1/2 transform -translate-y-1/2 text-white" />
                        <input
                            ref={inputRef}
                            type="text"
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            onKeyDown={handleKeyDown}
                            placeholder="Trova Giochi, Temi e Produttori"
                            className="w-full text-white border-none bg-transparent pl-7 pr-4 focus:outline-none search-placeholder"
                        />
                    </div>
                </div>
                {/* Linea divisoria */}
                <div className="border-t border-white mt-2" style={{borderWidth: '0.5px'}}></div>
            </div>

            {/* Contenuto scrollabile */}
            <div className="flex-1 overflow-y-auto min-h-0">
                {/* Mostra suggerimenti quando non c'è query */}
                {query.length === 0 && suggestions && suggestions.length > 0 && (
                    <div className="px-6 py-6 pb-24">
                        <div className="flex items-center mb-4">
                            <h2 className="text-white/70 text-[15px] font-medium uppercase pt-1.5 ml-1.5">SCELTO DA SNAI</h2>
                        </div>
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 justify-items-center">
                            {suggestions.map((game) => (
                                <GameCard
                                    key={game.id}
                                    game={game}
                                    size="medium"
                                    onPlay={onGamePlay}
                                    onInfo={handleGameCardClick}
                                />
                            ))}
                        </div>
                    </div>
                )}

                {/* Indica di digitare almeno 3 caratteri */}
                {query.length > 0 && query.length < 3 && (
                    <div className="text-center py-12 text-gray-400">
                        <p className="text-lg">Digita almeno 3 caratteri per cercare</p>
                    </div>
                )}

                {/* Loading */}
                {isLoading && (
                    <div className="text-center py-12">
                        <Loader2 size={32} className="mx-auto animate-spin text-white mb-4" />
                        <p className="text-white">Ricerca in corso...</p>
                    </div>
                )}

                {/* Errore */}
                {error && (
                    <div className="text-center py-12">
                        <div className="text-white text-xl mb-4">⚠️</div>
                        <p className="text-gray-300">{error}</p>
                        <button
                            onClick={() => setError(null)}
                            className="mt-4 bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded transition-colors"
                        >
                            Riprova
                        </button>
                    </div>
                )}

                {/* Risultati - griglia come nei menu principali */}
                {!isLoading && !error && query.length >= 3 && filteredResults.length > 0 && (
                    <div className="px-6 py-6 pb-24">
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 justify-items-center">
                            {filteredResults.map((game) => (
                                <GameCard
                                    key={game.id}
                                    game={game}
                                    size="medium"
                                    onPlay={onGamePlay}
                                    onInfo={handleGameCardClick}
                                />
                            ))}
                        </div>
                    </div>
                )}

                {/* Nessun risultato */}
                {!isLoading && !error && query.length >= 3 && filteredResults.length === 0 && lastSearchQuery !== '' && (
                    <div className="text-center py-12 text-gray-400">
                        <div className="text-6xl mb-4">🔍</div>
                        <p className="text-xl mb-2">Nessun gioco trovato</p>
                        <p>Prova con termini diversi per &quot;{query}&quot;</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default SearchPage;